<template>
  <div
    :class="[
      'home-cash-actions',
      { 'home-cash-actions--loaded': addLoadedClass }
    ]"
  >
    <div class="home-cash-actions__list">
      <div
        v-for="(action, index) in actions"
        :key="index"
        ref="actionItemsRef"
        :class="listItemClasses(action.disabled ?? false)"
      >
        <ButtonBoxLaunch
          :disabled="disableActions || action.disabled"
          :label="action.label"
          :loading="loading"
          :icon="action.icon"
          :is-blur="true"
          :data-testid="`cash-action-${action.label.toLowerCase()}`"
          :type="action.primary ? 'primary' : 'secondary'"
          @click="action.handler"
        />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import {
  Component,
  Vue,
  Prop,
  InjectReactive,
  Ref
} from 'vue-property-decorator';
import { Operation, userCanPerform } from '@/modules/common/services/partner';
import { isSuitabilityPending } from '@/router/helpers/shouldRedirectToSuitability';
import { Action, ActionType } from './types';
import {
  trackInvestClick,
  trackDepositClick,
  trackTransferClick
} from '../track-events';
import { ButtonBoxLaunch } from '@warrenbrasil/nebraska-web';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';

const ITEM_TRANSITION_BASE_DELAY = 400;

@Component({
  components: {
    ButtonBoxLaunch
  }
})
export default class HomeCashActions extends Vue {
  @Prop({ type: Boolean, required: false })
  readonly loading!: boolean;

  @Prop({ type: Boolean, required: false, default: false })
  readonly hasPositiveBalance!: boolean;

  @Prop({ type: String, required: true })
  readonly cashAccountId!: string;

  @Prop({ type: Boolean, required: false, default: false })
  readonly disableActions!: boolean;

  @Prop({ type: Boolean, required: true })
  readonly isOverdue!: boolean;

  @InjectReactive('shouldTriggerHomeAnimations')
  public addLoadedClass!: boolean;

  @Ref('actionItemsRef')
  readonly actionItemsRef?: HTMLDivElement[];

  mounted() {
    this.addAnimationDelayOnButtons();
  }

  listItemClasses(isDisable: boolean) {
    return [
      'home-cash-actions__item',
      isDisable && 'home-cash-actions__item--disabled'
    ];
  }

  get actions(): Action[] {
    return [
      {
        label: 'Investir',
        icon: 'BA0020',
        disabled: this.shouldDisableAction(ActionType.Invest),
        handler: this.goToInvestmentFlow,
        primary: true
      },
      {
        label: 'Depositar',
        icon: 'EA0021',
        disabled: this.shouldDisableAction(ActionType.Deposit),
        handler: this.depositAction
      },
      {
        label: 'Transferir',
        icon: 'AA0060',
        disabled: this.shouldDisableAction(ActionType.Transfer),
        handler: this.handleTransferClick
      }
    ];
  }

  private shouldDisableAction(action: ActionType) {
    switch (action) {
      case ActionType.Invest:
        return !this.hasPositiveBalance;
      case ActionType.Deposit:
        return isSuitabilityPending() || !userCanPerform(Operation.Deposit);
      case ActionType.Transfer:
        return (
          !this.hasPositiveBalance ||
          !userCanPerform(Operation.WithdrawToExternalAccount)
        );
      case ActionType.Pay:
      default:
        return false;
    }
  }

  private regulatoryUpdate() {
    this.$router.push({
      name: 'regulatoryUpdateAnnouncement'
    });
  }

  get isNewHomeInvestmentFlowEnabled() {
    return FeatureFlagsModule.isEnabled(
      FeatureFlags.ENABLE_NEW_HOME_INVESTMENT_FLOW
    );
  }

  private goToInvestmentFlow() {
    if (this.isOverdue) return this.regulatoryUpdate();

    this.$router.push(
      this.isNewHomeInvestmentFlowEnabled
        ? { name: 'investment', query: { cameFrom: 'home' } }
        : { name: 'wealthInvest' }
    );

    trackInvestClick();
  }

  private depositAction() {
    this.$router.push({ name: 'deposit-pix' });
    trackDepositClick();
  }

  handleTransferClick() {
    trackTransferClick();
    this.goToNewTransferPage();
  }

  private goToNewTransferPage() {
    this.$router.push({ name: 'new-transfer' });
  }

  private addAnimationDelayOnButtons() {
    this.actionItemsRef?.forEach((item, index) => {
      const delay = ITEM_TRANSITION_BASE_DELAY + index * 60;
      item.style.transitionDelay = `${delay}ms`;
    });
  }
}
</script>
<style lang="less" scoped>
@buttons-transition-timing-function: cubic-bezier(0.42, 0, 0.04, 1);

.home-cash-actions {
  &__list {
    display: flex;
    width: 100%;
  }

  &:not(&--loaded) &__item {
    opacity: 0;
    transform: translateY(180px);
  }

  &--loaded &__item {
    transition: opacity, transform 1s @buttons-transition-timing-function;
    opacity: 1;
    transform: translateY(0px);
  }

  &__item {
    margin-right: @size-spacing-x300;

    &--disabled {
      cursor: not-allowed;
    }

    &:last-of-type {
      margin-right: 0;
    }
  }

  .to-md({
    width: 100%;

    &__list {
      width: 100%;
      box-sizing: content-box;
      padding: @size-spacing-x150 @size-spacing-x400 @size-spacing-x350 0;
      margin: calc(@size-spacing-x150   -1) 0 calc(@size-spacing-x350   -1);
      overflow: auto;
    }
  });
}
</style>
